import {
  navigation_logo,
  whitepaper,
  about_us,
  lxp_thumbnail,
  aiform_thumbnail,
  lxp_benefits
} from "../../assets/images";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { IoMdArrowDropdown } from "react-icons/io";

/* ====== CONFIG ====== */
const BREAKPOINT = 1020; // px – switch to hamburger at and below this width

const keyActivate = (handler) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    handler?.(e);
  }
};

/* ====== Styled ====== */
const NavigationWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  display: flex;
  justify-content: center;
  background: transparent;
  padding: 16px 0;
  @media (max-width: ${BREAKPOINT}px) {
    padding: 0;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1100px;
  padding: 16px 20px;
  align-items: center;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;

  @media (max-width: ${BREAKPOINT}px) {
    border-radius: 0;
    max-width: 100%;
    padding: 14px 16px;
  }
`;

const NavigationContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const ButtonContainer = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
  gap: 8px;
  @media (max-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const Button = styled.button`
  --active: ${(p) => (p.$active ? 1 : 0)};
  color: ${(p) => (p.$active ? "#ffffffff" : "#6a37e3")};
  display: flex;
  height: 48px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 16px;
  border: 1.5px solid #6a37e3;
  background: ${(p) => (p.$active ? "#6a37e3" : "rgba(255, 255, 255, 0.9)")};
  cursor: pointer;
  user-select: none;
`;

const ButtonText = styled.span`
  font: 700 15px/140% Inter, sans-serif;
`;

const LogoImage = styled.img`
  height: 45px;
  width: auto;
  object-fit: contain;
  user-select: none;
  display: block;
`;

const LinksContainer = styled.nav`
  display: flex;
  gap: 30px;

  @media (max-width: ${BREAKPOINT}px) {
    display: none; /* hide desktop links on mobile */
  }
`;

/* Styled <a> for real navigational links */
const NavLink = styled.a`
  --active: ${(p) => (p.$active ? 1 : 0)};
  color: ${(p) => (p.$active ? "#6a37e3" : "#1c1b1f")};
  font: ${(p) =>
    p.$active
      ? "700 16px/140% Inter, sans-serif"
      : "500 16px/140% Inter, sans-serif"};
  text-decoration: none;
  cursor: pointer;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  user-select: none;
  &:after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -8px;
    height: 2px;
    width: ${(p) => (p.$active ? "100%" : "0")};
    background: #6a37e3;
    transition: width 0.2s ease;
  }
`;

/* Styled <button> for dropdown toggles, but visually same as a link */
const NavButton = styled.button`
  --active: ${(p) => (p.$active ? 1 : 0)};
  color: ${(p) => (p.$active ? "#6a37e3" : "#1c1b1f")};
  font: ${(p) =>
    p.$active
      ? "700 16px/140% Inter, sans-serif"
      : "500 16px/140% Inter, sans-serif"};
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  user-select: none;
  &:after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -8px;
    height: 2px;
    width: ${(p) => (p.$active ? "100%" : "0")};
    background: #6a37e3;
    transition: width 0.2s ease;
  }
`;

const NavigationLink = styled.a`
  display: block;
  padding: 8px 10px;
  border-radius: 8px;
  color: ${(p) => (p.$active ? "#6a37e3" : "#1c1b1f")};
  font: 500 15px/140% Inter, sans-serif;
  text-decoration: none;
  cursor: pointer;
  background: ${(p) => (p.$active ? "rgba(106,55,227,0.08)" : "transparent")};
`;

/* Desktop dropdown layer (visually fixed/centered) – hidden on mobile
   NOTE: This is rendered inline after the trigger, so DOM order is correct. */
const RootDropdownLayer = styled.div`
  position: fixed;
  top: 96px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 999;

  @media (max-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const DropdownContainer = styled.section`
  width: 100%;
  max-width: 1135px;
  height: 286px;
  position: relative;
  display: flex;
  justify-content: space-between;
`;

const DropdownBackground = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  position: absolute;
  top: 0;
  left: 0;
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
`;

const DropdownNav = styled.ul`
  display: flex;
  width: 220px;
  flex-direction: column;
  position: relative;
  padding: 18px 10px 0 25px;
  margin: 0;
  list-style: none;
`;

const NavigationItem = styled.li`
  padding: 4px 0;
  border-bottom: 0.1px solid #0000001c;
`;

const NavigationItemWithChildren = styled.li`
  padding: 4px 0;
  border-bottom: 0.1px solid #0000001c;
`;

const SubMenuContainer = styled.div`
  padding-left: 12px;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SubMenuItem = styled.a`
  display: block;
  padding: 6px 10px;
  border-radius: 6px;
  color: ${(p) => (p.$active ? "#6a37e3" : "#555")};
  font: 500 14px/140% Inter, sans-serif;
  text-decoration: none;
  cursor: pointer;
  background: ${(p) => (p.$active ? "rgba(106,55,227,0.06)" : "transparent")};
  &:hover {
    background: rgba(106,55,227,0.08);
  }
`;

const ParentMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 8px;
  color: ${(p) => (p.$active ? "#6a37e3" : "#1c1b1f")};
  font: 600 15px/140% Inter, sans-serif;
  cursor: pointer;
  background: ${(p) => (p.$active ? "rgba(106,55,227,0.08)" : "transparent")};
  &:hover {
    background: rgba(106,55,227,0.05);
  }
`;

const FeaturedCardsSection = styled.section`
  display: flex;
  width: 100%;
  gap: 12px;
  position: relative;
  padding: 17px 25px;
  justify-content: end;
`;

const BlogCardWrapper = styled.div`
  width: 100%;
  max-width: 328px;
  height: 253px;
  cursor: pointer;
`;

const CardContainer = styled.article`
  width: 100%;
  height: 100%;
  position: relative;
`;

const CardBackground = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
`;

const CategoryLabel = styled.p`
  font: 500 15px/140% Inter, sans-serif;
  color: #fff;
  position: absolute;
  left: 23px;
  top: 13px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.88);
`;

const CardDecription = styled.p`
  font: 500 15px/140% Inter, sans-serif;
  color: #fff;
  position: absolute;
  left: 23px;
  bottom: 7px;
  width: 70%;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.88);
`;

function FeaturedCard({ category, description, backgroundImage }) {
  return (
    <CardContainer role="img" aria-label="illustration">
      <CardBackground
        role="img"
        aria-label="illustration"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      <CategoryLabel>{category}</CategoryLabel>
      <CardDecription>{description}</CardDecription>
    </CardContainer>
  );
}

/* ====== Mobile hamburger & menu ====== */
const HamburgerBtn = styled.button`
  display: none;
  @media (max-width: ${BREAKPOINT}px) {
    display: inline-flex;
  }
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e5e5e5;
  cursor: pointer;
`;

const Bar = styled.span`
  position: relative;
  width: 20px;
  height: 2.5px;
  background: #6a37e3;
  display: block;
  &:before,
  &:after {
    content: "";
    position: absolute;
    left: 0;
    width: 20px;
    height: 2.5px;
    background: #6a37e3;
  }
  &:before {
    top: -6px;
  }
  &:after {
    top: 6px;
  }
`;

/* Simple slide-down panel */
const MobileMenu = styled.div`
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  z-index: 999;
  overflow-y: auto;
  border-top: 1px solid #eee;
  padding: 12px 16px;

  @media (min-width: ${BREAKPOINT + 1}px) {
    display: none;
  }
`;

const MobileGroup = styled.div`
  margin: 8px 0 4px;
`;

const MobileItem = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
  font: 600 16px/140% Inter, sans-serif;
  color: ${(p) => (p.$active ? "#6a37e3" : "#1c1b1f")};
  padding: 12px 8px;
  border-radius: 10px;
  &:hover {
    background: #f7f5ff;
  }
`;

const MobileSubItem = styled.button`
  width: 100%;
  text-align: left;
  margin-left: 15px;
  background: transparent;
  border: none;
  cursor: pointer;
  font: 500 15px/140% Inter, sans-serif;
  color: ${(p) => (p.$active ? "#6a37e3" : "#1c1b1f")};
  padding: 10px 10px;
  border-radius: 10px;
  &:hover {
    background: #fafafa;
  }
`;

const MobileAccordionBtn = styled(MobileItem)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Caret = styled.span`
  font-size: 20px;
  line-height: 1;
  transform: rotate(${(p) => (p.$open ? "180deg" : "0deg")});
  transition: transform 0.15s ease;
`;

const StickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: #fff;
  padding: 12px 8px;
  border-top: 1px solid #eee;
`;

/* ====== Data ====== */
const DROPDOWN_CONTENT = {
  products: [
    {
      name: "LXP",
      link: "https://ailms.campusinsights.ai/welcome",
      description: "Learning eXperience Platform – smarter learning paths, seamless content flow",
      image: lxp_thumbnail
    },
    {
      name: "Smart Forms",
      link: "/products/smart-forms",
      description: "Simplifying Data and form Processes",
      image: aiform_thumbnail
    },
    {
      name: "AI Assistant",
      link: "/products/ai-assistant",
      description: "Your Campus Digital Concierge",
      image: lxp_benefits
    }
  ],
  blogs: [
    {
      name: "LXP",
      link: null, // Parent menu, no direct link
      description: "Learning eXperience Platform blogs",
      image: lxp_thumbnail,
      children: [
        {
          name: "Educators",
          link: "/blogs/ai-lms",
          description: "Resources for educators",
          image: lxp_thumbnail
        },
        {
          name: "Students",
          link: "/blogs/ai-lms-students",
          description: "Resources for students",
          image: lxp_thumbnail
        }
      ]
    },
    {
      name: "Smart Forms Blogs",
      link: "/blogs/smart-forms",
      description: "Simplifying Data and form Processes",
      image: aiform_thumbnail
    },
    {
      name: "AI Assistant Blogs",
      link: "/blogs/ai-assistant",
      description: "Your Campus Digital Concierge",
      image: lxp_benefits
    }
  ],
  resources: [
    {
      name: "White papers",
      link: "/resources/whitepaper",
      description: "Docs",
      image: whitepaper
    },
    { name: "About Us", link: "/resources/about_us", description: "Company", image: about_us }
  ]
};

/* ====== DropdownMenu (inline-rendered, visually fixed) ====== */
function DropdownMenu({ id, label, items, onRequestClose, triggerRef, navigateAndClose, isPathActive, expandedParent, setExpandedParent }) {
  const layerRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    // focus first item on open
    if (itemRefs.current[0]) itemRefs.current[0].focus();

    // close on outside click
    const onDocDown = (e) => {
      const t = e.target;
      if (!layerRef.current?.contains(t) && !triggerRef.current?.contains(t)) {
        onRequestClose();
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [onRequestClose, triggerRef]);

  const move = (currentIdx, dir) => {
    const len = items.length;
    const next = (currentIdx + dir + len) % len;
    itemRefs.current[next]?.focus();
  };

  const onMenuKeyDown = (e) => {
    const idx = itemRefs.current.findIndex((n) => n === document.activeElement);
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        onRequestClose();
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        move(Math.max(0, idx), +1);
        break;
      case "ArrowUp":
        e.preventDefault();
        move(Math.max(0, idx), -1);
        break;
      case "Home":
        e.preventDefault();
        itemRefs.current[0]?.focus();
        break;
      case "End":
        e.preventDefault();
        itemRefs.current[items.length - 1]?.focus();
        break;
      case "Tab": {
        const first = itemRefs.current[0];
        const last = itemRefs.current[items.length - 1];
        const onFirst = document.activeElement === first;
        const onLast = document.activeElement === last;

        if (e.shiftKey && onFirst) {
          // Shift+Tab back to trigger
          e.preventDefault();
          onRequestClose();
          triggerRef.current?.focus();
        } else if (!e.shiftKey && onLast) {
          // let Tab continue; close menu so focus proceeds to next main item
          onRequestClose();
        }
        break;
      }
      default:
        break;
    }
  };

  const toggleParent = (parentName) => {
    setExpandedParent(expandedParent === parentName ? null : parentName);
  };

  // Flatten items for keyboard navigation, but render with hierarchy
  const flattenItems = (items) => {
    const flat = [];
    items.forEach((item) => {
      if (item.children) {
        flat.push({ ...item, isParent: true });
        item.children.forEach((child) => {
          flat.push({ ...child, isChild: true, parentName: item.name });
        });
      } else {
        flat.push(item);
      }
    });
    return flat;
  };

  const flatItems = flattenItems(items);

  return (
    <RootDropdownLayer ref={layerRef} role="presentation">
      <DropdownContainer>
        <DropdownBackground />
        <DropdownNav ref={menuRef} role="menu" id={id} aria-label={label} onKeyDown={onMenuKeyDown}>
          {items.map((item, i) => {
            if (item.children) {
              // Parent menu item with children (LXP)
              const isExpanded = expandedParent === item.name;
              const hasActiveChild = item.children.some((child) => isPathActive(child.link));

              return (
                <NavigationItemWithChildren key={item.name}>
                  <ParentMenuHeader
                    ref={(el) => (itemRefs.current[i] = el)}
                    $active={hasActiveChild}
                    onClick={() => toggleParent(item.name)}
                    onKeyDown={keyActivate(() => toggleParent(item.name))}
                    tabIndex={0}
                    role="button"
                    aria-expanded={isExpanded}
                  >
                    {item.name}
                    <IoMdArrowDropdown
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    />
                  </ParentMenuHeader>
                  {isExpanded && (
                    <SubMenuContainer role="group" aria-label={`${item.name} sub-menu`}>
                      {item.children.map((child, childIdx) => {
                        const activeChild = isPathActive(child.link);
                        return (
                          <SubMenuItem
                            key={child.link}
                            href={child.link}
                            $active={activeChild}
                            aria-current={activeChild ? "page" : undefined}
                            onClick={(e) => {
                              e.preventDefault();
                              onRequestClose();
                              navigateAndClose(child.link);
                            }}
                            onKeyDown={keyActivate(() => {
                              onRequestClose();
                              navigateAndClose(child.link);
                            })}
                          >
                            {child.name}
                          </SubMenuItem>
                        );
                      })}
                    </SubMenuContainer>
                  )}
                </NavigationItemWithChildren>
              );
            } else {
              // Regular menu item
              const setRef = (el) => (itemRefs.current[i] = el);
              const activeChild = isPathActive(item.link);
              return (
                <NavigationItem key={item.link}>
                  <NavigationLink
                    ref={setRef}
                    href={item.link}
                    role="menuitem"
                    tabIndex={0}
                    $active={activeChild}
                    aria-current={activeChild ? "page" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      onRequestClose();
                      navigateAndClose(item.link);
                    }}
                    onKeyDown={keyActivate(() => {
                      onRequestClose();
                      navigateAndClose(item.link);
                    })}
                  >
                    {item.name}
                  </NavigationLink>
                </NavigationItem>
              );
            }
          })}
        </DropdownNav>

        {/* Keep these OUT of the a11y tree & tab order */}
        <FeaturedCardsSection aria-hidden="true">
          {items.map((item) => {
            // For parent items (LXP), show the Educators card (first child) in the header
            const displayItem = item.children ? item.children[0] : item;

            return (
              <BlogCardWrapper
                key={`${id}-card-${item.link || item.name}`}
                role="presentation"
                tabIndex={-1}
                onClick={() => {
                  if (item.children) {
                    // If LXP clicked, navigate to first child (Educators)
                    navigateAndClose(item.children[0].link);
                  } else {
                    navigateAndClose(item.link);
                  }
                }}
              >
                <FeaturedCard
                  category={displayItem.name}
                  description={displayItem.description}
                  backgroundImage={displayItem.image || whitepaper}
                />
              </BlogCardWrapper>
            );
          })}
        </FeaturedCardsSection>
      </DropdownContainer>
    </RootDropdownLayer>
  );
}

/* ====== Component ====== */
const Navigation = () => {
  const [activeMenu, setActiveMenu] = useState(""); // "products" | "blogs" | "resources" | ""
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState("");
  const [expandedParent, setExpandedParent] = useState(null); // For desktop blogs dropdown
  const productsBtnRef = useRef(null);
  const blogsBtnRef = useRef(null);
  const resourcesBtnRef = useRef(null);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isPathActive = useCallback(
    (path) => pathname === path || pathname.startsWith(path + "/"),
    [pathname]
  );

  const closeDropdown = useCallback(() => {
    setActiveMenu("");
    setExpandedParent(null);
  }, []);

  const openDropdown = useCallback(
    (menu) => {
      setActiveMenu((curr) => (curr === menu ? "" : menu));
      setExpandedParent(null); // Reset parent expansion when opening new menu
    },
    []
  );

  const navigateAndClose = useCallback(
    (path) => {
      closeDropdown();
      setMobileOpen(false);
      setMobileSub("");
      if (path.startsWith("http")) {
        window.open(path, "_blank");
      } else {
        window.scrollTo(0, 0);
        navigate(path);
      }
    },
    [closeDropdown, navigate]
  );

  /* ESC at document level to close current dropdown */
  useEffect(() => {
    if (!activeMenu) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeDropdown();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [activeMenu, closeDropdown]);

  /* Lock body scroll when mobile menu is open */
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  /* Expand correct accordion in mobile view based on current path */
  useEffect(() => {
    if (isPathActive("/products")) setMobileSub("products");
    else if (isPathActive("/resources")) setMobileSub("resources");
    else if (isPathActive("/blogs")) setMobileSub("blogs");
    else setMobileSub("");
  }, [pathname, isPathActive]);

  // Check if LXP children are active for mobile
  const isLXPActive = isPathActive("/blogs/ai-lms") || isPathActive("/blogs/ai-lms-students");
  const isLXPExpanded = mobileSub === "blogs-lxp";

  return (
    <>
      <NavigationWrapper>
        <NavigationContainer>
          <NavigationContent>
            {/* Logo: real link + preventDefault for SPA nav */}
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigateAndClose("/");
              }}
              aria-label="Go to Home"
            >
              <LogoImage src={navigation_logo} alt="Campus Insights" />
            </a>

            {/* Desktop links */}
            <LinksContainer aria-label="Primary">
              {/* Home */}
              <NavLink
                href="/"
                $active={pathname === "/"}
                onClick={(e) => {
                  e.preventDefault();
                  navigateAndClose("/");
                }}
                aria-current={pathname === "/" ? "page" : undefined}
              >
                Home
              </NavLink>

              {/* Products trigger + inline menu */}
              <NavButton
                ref={productsBtnRef}
                $active={isPathActive("/products")}
                aria-expanded={activeMenu === "products"}
                aria-haspopup="menu"
                aria-controls="menu-products"
                onClick={() => openDropdown("products")}
                onKeyDown={keyActivate(() => openDropdown("products"))}
              >
                Products <IoMdArrowDropdown />
              </NavButton>
              {activeMenu === "products" && (
                <DropdownMenu
                  id="menu-products"
                  label="Products menu"
                  items={DROPDOWN_CONTENT.products}
                  onRequestClose={closeDropdown}
                  triggerRef={productsBtnRef}
                  navigateAndClose={navigateAndClose}
                  isPathActive={isPathActive}
                  expandedParent={expandedParent}
                  setExpandedParent={setExpandedParent}
                />
              )}

              {/* Students */}
              <NavLink
                href="/students"
                $active={isPathActive("/students")}
                onClick={(e) => {
                  e.preventDefault();
                  navigateAndClose("/students");
                }}
                aria-current={isPathActive("/students") ? "page" : undefined}
              >
                Students
              </NavLink>

              {/* Colleges */}
              <NavLink
                href="/colleges"
                $active={isPathActive("/colleges")}
                onClick={(e) => {
                  e.preventDefault();
                  navigateAndClose("/colleges");
                }}
                aria-current={isPathActive("/colleges") ? "page" : undefined}
              >
                Colleges
              </NavLink>

              {/* Customer Stories */}
              <NavLink
                href="/customer_stories"
                $active={isPathActive("/customer_stories")}
                onClick={(e) => {
                  e.preventDefault();
                  navigateAndClose("/customer_stories");
                }}
                aria-current={isPathActive("/customer_stories") ? "page" : undefined}
              >
                Customer Stories
              </NavLink>

              {/* Blogs trigger + inline menu */}
              <NavButton
                ref={blogsBtnRef}
                $active={isPathActive("/blogs")}
                aria-expanded={activeMenu === "blogs"}
                aria-haspopup="menu"
                aria-controls="menu-blogs"
                onClick={() => openDropdown("blogs")}
                onKeyDown={keyActivate(() => openDropdown("blogs"))}
              >
                Blogs <IoMdArrowDropdown />
              </NavButton>
              {activeMenu === "blogs" && (
                <DropdownMenu
                  id="menu-blogs"
                  label="Blogs menu"
                  items={DROPDOWN_CONTENT.blogs}
                  onRequestClose={closeDropdown}
                  triggerRef={blogsBtnRef}
                  navigateAndClose={navigateAndClose}
                  isPathActive={isPathActive}
                  expandedParent={expandedParent}
                  setExpandedParent={setExpandedParent}
                />
              )}

              {/* Resources trigger + inline menu */}
              <NavButton
                ref={resourcesBtnRef}
                $active={isPathActive("/resources")}
                aria-expanded={activeMenu === "resources"}
                aria-haspopup="menu"
                aria-controls="menu-resources"
                onClick={() => openDropdown("resources")}
                onKeyDown={keyActivate(() => openDropdown("resources"))}
              >
                Resources <IoMdArrowDropdown />
              </NavButton>
              {activeMenu === "resources" && (
                <DropdownMenu
                  id="menu-resources"
                  label="Resources menu"
                  items={DROPDOWN_CONTENT.resources}
                  onRequestClose={closeDropdown}
                  triggerRef={resourcesBtnRef}
                  navigateAndClose={navigateAndClose}
                  isPathActive={isPathActive}
                  expandedParent={expandedParent}
                  setExpandedParent={setExpandedParent}
                />
              )}
            </LinksContainer>

            {/* Desktop CTA */}
            <ButtonContainer onClick={() => navigateAndClose("/contact")}>
              <Button $active={isPathActive("/contact")}>
                <ButtonText>Contact Us</ButtonText>
              </Button>
            </ButtonContainer>

            {/* Hamburger (mobile only) */}
            <HamburgerBtn
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? (
                <AiOutlineClose style={{ width: "25px", height: "25px", color: "#6a37e3" }} />
              ) : (
                <Bar />
              )}
            </HamburgerBtn>
          </NavigationContent>
        </NavigationContainer>
      </NavigationWrapper>

      {/* Mobile slide-down menu */}
      {mobileOpen && (
        <MobileMenu id="mobile-menu" role="dialog" aria-modal="true" aria-label="Mobile Navigation">
          <MobileGroup>
            <MobileItem $active={pathname === "/"} onClick={() => navigateAndClose("/")}>
              Home
            </MobileItem>

            {/* Products */}
            <MobileAccordionBtn
              $active={isPathActive("/products")}
              onClick={() => setMobileSub((s) => (s === "products" ? "" : "products"))}
              aria-expanded={mobileSub === "products"}
              aria-controls="mobile-products"
            >
              Products <Caret $open={mobileSub === "products"}> <IoMdArrowDropdown /></Caret>
            </MobileAccordionBtn>
            {mobileSub === "products" && (
              <div id="mobile-products" role="group" aria-label="Products">
                {DROPDOWN_CONTENT.products.map((p) => (
                  <MobileSubItem key={p.link} $active={isPathActive(p.link)} onClick={() => navigateAndClose(p.link)}>
                    {p.name}
                  </MobileSubItem>
                ))}
              </div>
            )}

            <MobileItem $active={isPathActive("/students")} onClick={() => navigateAndClose("/students")}>
              Students
            </MobileItem>

            <MobileItem $active={isPathActive("/colleges")} onClick={() => navigateAndClose("/colleges")}>
              Colleges
            </MobileItem>

            <MobileItem
              $active={isPathActive("/customer_stories")}
              onClick={() => navigateAndClose("/customer_stories")}
            >
              Customer Stories
            </MobileItem>

            {/* Blogs */}
            <MobileAccordionBtn
              $active={isPathActive("/blogs")}
              onClick={() => setMobileSub((s) => (s === "blogs" ? "" : "blogs"))}
              aria-expanded={mobileSub === "blogs"}
              aria-controls="mobile-blogs"
            >
              Blogs <Caret $open={mobileSub === "blogs"}> <IoMdArrowDropdown /></Caret>
            </MobileAccordionBtn>
            {mobileSub === "blogs" && (
              <div id="mobile-blogs" role="group" aria-label="blogs">
                {/* LXP Parent with nested children */}
                <MobileAccordionBtn
                  $active={isLXPActive}
                  onClick={() => setMobileSub((s) => (s === "blogs-lxp" ? "blogs" : "blogs-lxp"))}
                  aria-expanded={isLXPExpanded}
                  aria-controls="mobile-blogs-lxp"
                  style={{ marginLeft: "15px", fontWeight: 500 }}
                >
                  LXP <Caret $open={isLXPExpanded}> <IoMdArrowDropdown /></Caret>
                </MobileAccordionBtn>
                {isLXPExpanded && (
                  <div id="mobile-blogs-lxp" role="group" aria-label="LXP Blogs">
                    {DROPDOWN_CONTENT.blogs[0].children.map((child) => (
                      <MobileSubItem
                        key={child.link}
                        $active={isPathActive(child.link)}
                        onClick={() => navigateAndClose(child.link)}
                        style={{ marginLeft: "30px" }}
                      >
                        {child.name}
                      </MobileSubItem>
                    ))}
                  </div>
                )}

                {/* Other blog items */}
                {DROPDOWN_CONTENT.blogs.slice(1).map((r) => (
                  <MobileSubItem key={r.link} $active={isPathActive(r.link)} onClick={() => navigateAndClose(r.link)}>
                    {r.name}
                  </MobileSubItem>
                ))}
              </div>
            )}

            {/* Resources */}
            <MobileAccordionBtn
              $active={isPathActive("/resources")}
              onClick={() => setMobileSub((s) => (s === "resources" ? "" : "resources"))}
              aria-expanded={mobileSub === "resources"}
              aria-controls="mobile-resources"
            >
              Resources <Caret $open={mobileSub === "resources"}> <IoMdArrowDropdown /></Caret>
            </MobileAccordionBtn>
            {mobileSub === "resources" && (
              <div id="mobile-resources" role="group" aria-label="Resources">
                {DROPDOWN_CONTENT.resources.map((r) => (
                  <MobileSubItem key={r.link} $active={isPathActive(r.link)} onClick={() => navigateAndClose(r.link)}>
                    {r.name}
                  </MobileSubItem>
                ))}
              </div>
            )}
          </MobileGroup>

          <StickyFooter>
            <MobileItem
              $active={isPathActive("/contact")}
              onClick={() => navigateAndClose("/contact")}
              aria-label="Contact Us"
            >
              Contact Us
            </MobileItem>
          </StickyFooter>
        </MobileMenu>
      )}
    </>
  );
};

export default Navigation;
